import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Wallet, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { canUseWithdrawals, withdrawalMessage } from '../../lib/accountPermissions';

const defaultForm = (currency = 'UGX') => ({
  amount: '',
  currency,
  reason: '',
  withdrawal_method: 'mobile_money',
});

function StatusBadge({ status }) {
  const s = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s[status] || 'bg-slate-100 text-slate-800'}`}>{status}</span>;
}

function SettlementBadge({ withdrawal }) {
  const mode = String(withdrawal?.settlement_mode || '').toLowerCase();
  const status = String(withdrawal?.settlement_status || '').toLowerCase();
  if (withdrawal?.settled_directly_to_merchant || mode === 'flutterwave_split') {
    const label = status === 'pending' ? 'Split settlement pending' : 'Split-settled to merchant';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">{label}</span>;
  }
  if (mode === 'jjuma_wallet') {
    const label = status === 'pending_wallet_fallback' ? 'Wallet settlement pending' : 'Paid via JJuma wallet';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">{label}</span>;
  }
  if (mode === 'manual') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">Manual review</span>;
  }
  return null;
}

export default function Withdrawals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ withdrawals: [], total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [detailWithdrawal, setDetailWithdrawal] = useState(null);
  const [form, setForm] = useState(defaultForm(user?.default_currency || 'UGX'));
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [quoteState, setQuoteState] = useState({ loading: false, quote: null, error: '', signature: '' });
  const [bankDetails, setBankDetails] = useState(null);
  const quoteRequestId = useRef(0);
  const canWithdraw = canUseWithdrawals(user);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      currency: user?.default_currency || prev.currency || 'UGX',
    }));
  }, [user?.default_currency]);

  const fetchWithdrawals = useCallback(() => {
    if (!canWithdraw) {
      setLoading(false);
      return;
    }
    api.get('/withdrawals').then(r => {
      setData(r.data);
      setSummary(r.data?.summary || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [canWithdraw]);

  const fetchBankDetails = useCallback(() => {
    if (!canWithdraw) return;
    api.get('/bank-details').then(r => setBankDetails(r.data)).catch(() => {});
  }, [canWithdraw]);

  useEffect(() => { fetchWithdrawals(); }, [fetchWithdrawals]);
  useEffect(() => { fetchBankDetails(); }, [fetchBankDetails]);

  const normalizeAmount = (value) => {
    const amount = Number.parseFloat(value);
    return Number.isFinite(amount) ? amount : 0;
  };

  const formatMoney = (currency, value) => `${currency} ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const fetchQuote = useCallback(async (nextForm, requestId) => {
    const amount = normalizeAmount(nextForm.amount);
    const signature = `${amount}|${nextForm.currency}|${nextForm.withdrawal_method}`;
    if (!amount || amount <= 0) {
      setQuoteState({ loading: false, quote: null, error: '', signature: '' });
      return null;
    }

    try {
      const { data: quoteRes } = await api.post('/withdrawals/quote', {
        amount,
        currency: nextForm.currency,
        withdrawal_method: nextForm.withdrawal_method,
      });
      if (requestId && requestId !== quoteRequestId.current) return null;
      setQuoteState({ loading: false, quote: quoteRes.quote, error: '', signature });
      return quoteRes.quote;
    } catch (err) {
      if (requestId && requestId !== quoteRequestId.current) return null;
      const detail = err.response?.data?.detail || 'Failed to calculate withdrawal fee';
      setQuoteState({ loading: false, quote: null, error: detail, signature });
      return null;
    }
  }, []);

  const currentQuote = quoteState.quote;
  const methodLabel = form.withdrawal_method === 'bank_transfer' ? 'Bank Transfer' : 'Mobile Money';
  const parsedAmount = normalizeAmount(form.amount);
  const quoteSignature = `${parsedAmount}|${form.currency}|${form.withdrawal_method}`;
  const hasAmount = parsedAmount > 0;
  const totalFee = currentQuote?.total_fee ?? currentQuote?.fee_amount ?? 0;
  const netAmount = currentQuote?.net_amount ?? Math.max(parsedAmount - totalFee, 0);
  const fixedFee = currentQuote?.fixed_withdrawal_fee ?? currentQuote?.fixed_fee ?? 0;
  const minimumAmount = currentQuote?.minimum_amount ?? 0;
  const amountTooLow = !!quoteState.error && quoteState.error.toLowerCase().includes('minimum');
  const feeTooHigh = !!quoteState.error && quoteState.error.toLowerCase().includes('too high');
  const hasFreshQuote = !!currentQuote && quoteState.signature === quoteSignature;
  const canSubmit = canWithdraw && hasAmount && hasFreshQuote && !quoteState.loading && !quoteState.error && netAmount > 0;

  const methodBankDetails = form.withdrawal_method === 'bank_transfer'
    ? {
        primary: bankDetails?.bank_name || '-',
        secondary: bankDetails?.account_name || '-',
        tertiary: bankDetails?.account_number || '-',
      }
    : {
        primary: bankDetails?.mobile_money_number || '-',
        secondary: 'Mobile Money Number',
      };

  useEffect(() => {
    if (!showRequest) return undefined;
    if (!hasAmount) {
      setQuoteState({ loading: false, quote: null, error: '', signature: '' });
      return undefined;
    }
    const requestId = ++quoteRequestId.current;
    setQuoteState(prev => ({ ...prev, loading: true, error: '' }));
    fetchQuote(form, requestId);
    return () => {
      if (quoteRequestId.current === requestId) {
        quoteRequestId.current += 1;
      }
    };
  }, [form, hasAmount, showRequest, fetchQuote]);

  const handlePreviewSubmit = async (e) => {
    e.preventDefault();
    const requestId = ++quoteRequestId.current;
    const quote = currentQuote || await fetchQuote(form, requestId);
    if (quote) {
      setConfirmOpen(true);
    }
  };

  const confirmWithdrawal = async () => {
    setSubmitting(true);
    try {
      await api.post('/withdrawals', {
        amount: parsedAmount,
        currency: form.currency,
        reason: form.reason,
        withdrawal_method: form.withdrawal_method,
      });
      toast.success('Withdrawal request submitted!');
      setConfirmOpen(false);
      setShowRequest(false);
      setForm(defaultForm(user?.default_currency || 'UGX'));
      setQuoteState({ loading: false, quote: null, error: '', signature: '' });
      quoteRequestId.current += 1;
      fetchWithdrawals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Withdrawals</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage your withdrawal requests</p>
        </div>
        <Button onClick={() => (canWithdraw ? setShowRequest(true) : null)} disabled={!canWithdraw} className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50" data-testid="request-withdrawal-btn">
          <Plus size={16} className="mr-1.5" /> Request Withdrawal
        </Button>
      </div>

      {!canWithdraw && (
        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">{withdrawalMessage()}</p>
              <p className="text-xs text-amber-800 mt-0.5">Ask an admin to enable withdrawals on your account.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/verification')} className="shrink-0">
            Go to Verification
          </Button>
        </div>
      )}

      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm flex items-start gap-2" data-testid="withdrawal-notice">
        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
        <span>Withdrawals are processed within 48 working hours, excluding weekends and public holidays. Minimum withdrawal: UGX 50,000 or USD 20.</span>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Wallet Balance</p>
              <p className="text-xl font-bold text-slate-900 mono-nums">{formatMoney(summary.currency, summary.wallet_balance || 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Available for Withdrawal</p>
              <p className="text-xl font-bold text-emerald-700 mono-nums">{formatMoney(summary.currency, summary.available_for_withdrawal || 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Split-settled to merchant</p>
              <p className="text-xl font-bold text-blue-700 mono-nums">{formatMoney(summary.currency, summary.split_settled_amount || 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Pending Withdrawal</p>
              <p className="text-xl font-bold text-amber-700 mono-nums">{formatMoney(summary.currency, summary.pending_withdrawal || 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Completed Withdrawals</p>
              <p className="text-xl font-bold text-slate-900 mono-nums">{summary.completed_withdrawals || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Failed Withdrawals</p>
              <p className="text-xl font-bold text-slate-900 mono-nums">{summary.failed_withdrawals || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {summary && Number(summary.available_for_withdrawal || 0) <= 0 && Number(summary.split_settled_amount || 0) > 0 && (
        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          No withdrawable balance available. Some payments may have already been paid directly through Flutterwave split settlement.
        </div>
      )}

      <Dialog open={showRequest && canWithdraw} onOpenChange={v => { if (canWithdraw) setShowRequest(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Request Withdrawal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePreviewSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount</Label>
                <Input data-testid="withdrawal-amount-input" type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="50000" className="mt-1" required />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={v => setForm(p => ({ ...p, currency: v }))}>
                  <SelectTrigger className="mt-1" data-testid="withdrawal-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UGX">UGX</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Withdrawal Method</Label>
              <Select value={form.withdrawal_method} onValueChange={v => setForm(p => ({ ...p, withdrawal_method: v }))}>
                <SelectTrigger className="mt-1" data-testid="withdrawal-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3" data-testid="withdrawal-summary-card">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Withdrawal Summary</p>
                  <p className="text-xs text-slate-500">Live calculation updates as you type or switch method.</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${form.withdrawal_method === 'bank_transfer' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                  {methodLabel}
                </span>
              </div>
              {quoteState.loading ? (
                <p className="text-sm text-slate-500">Calculating charges...</p>
              ) : (
                <div className="space-y-2 text-sm text-slate-700">
                  <div className="flex items-center justify-between gap-4">
                    <span>Withdrawal Amount</span>
                    <span className="font-medium text-slate-900">{formatMoney(form.currency, currentQuote?.gross_amount ?? parsedAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span>Withdrawal Method</span>
                    <span className="font-medium text-slate-900">{methodLabel}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span>Withdrawal Fee Percentage</span>
                    <span className="font-medium text-slate-900">{(currentQuote?.fee_pct ?? currentQuote?.withdrawal_fee_percentage ?? 0).toFixed(2)}%</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span>Fixed Withdrawal Fee</span>
                    <span className="font-medium text-slate-900">{formatMoney(form.currency, fixedFee)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span>Total Fee</span>
                    <span className="font-semibold text-slate-900">{formatMoney(form.currency, totalFee)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4 border-t border-slate-200 pt-2">
                    <span className="font-medium">Amount You Will Receive</span>
                    <span className="font-semibold text-emerald-700">{formatMoney(form.currency, netAmount)}</span>
                  </div>
                  {form.withdrawal_method === 'bank_transfer' && (
                    <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-2">
                      Bank withdrawals may have different charges from Mobile Money.
                    </p>
                  )}
                </div>
              )}
              {(quoteState.error || !hasAmount) && (
                <div className={`rounded-lg border p-3 text-sm ${amountTooLow || feeTooHigh ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                  {quoteState.error || 'Enter an amount to see the withdrawal fee before you continue.'}
                  {amountTooLow && minimumAmount > 0 && (
                    <p className="mt-1 text-xs">Minimum withdrawal for this selection is {formatMoney(form.currency, minimumAmount)}.</p>
                  )}
                  {feeTooHigh && (
                    <p className="mt-1 text-xs">Withdrawal amount is too low after charges.</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Reason (optional)</Label>
              <Input data-testid="withdrawal-reason-input" value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Monthly payout" className="mt-1" />
            </div>

            {bankDetails && (
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 space-y-1 text-sm text-blue-900">
                <p className="font-semibold">Current payout details</p>
                {form.withdrawal_method === 'bank_transfer' ? (
                  <>
                    <p>Bank: {methodBankDetails.primary}</p>
                    <p>Account Name: {methodBankDetails.secondary}</p>
                    <p>Account Number: {methodBankDetails.tertiary}</p>
                  </>
                ) : (
                  <p>Mobile Money: {methodBankDetails.primary}</p>
                )}
              </div>
            )}

            <Button type="submit" data-testid="submit-withdrawal-btn" disabled={submitting || !canSubmit} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
              {submitting ? 'Submitting...' : 'Review Withdrawal'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={v => { if (!v) setConfirmOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Confirm Withdrawal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              You are requesting {formatMoney(form.currency, currentQuote?.gross_amount ?? parsedAmount)} through {methodLabel}.
            </p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1 text-sm text-slate-700">
              <p><span className="font-medium">Withdrawal charge:</span> {formatMoney(form.currency, currentQuote?.total_fee ?? currentQuote?.fee_amount ?? 0)}</p>
              <p><span className="font-medium">You will receive:</span> {formatMoney(form.currency, currentQuote?.net_amount ?? 0)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmOpen(false)} data-testid="cancel-withdrawal-confirm-btn">
                Cancel
              </Button>
              <Button onClick={confirmWithdrawal} disabled={submitting} className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="confirm-withdrawal-btn">
                {submitting ? 'Submitting...' : 'Confirm Withdrawal'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>
          ) : data.withdrawals.length === 0 ? (
            <div className="py-12 text-center">
              <Wallet size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No withdrawal requests yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Method</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Requested</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Charges</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Amount Received</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Account</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.withdrawals.map((w, i) => (
                    <TableRow key={i} className="hover:bg-slate-50/50" data-testid={`withdrawal-row-${i}`}>
                      <TableCell className="text-sm">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${w.withdrawal_method === 'bank_transfer' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {w.withdrawal_method === 'bank_transfer' ? 'Bank Transfer' : 'Mobile Money'}
                        </span>
                      </TableCell>
                      <TableCell className="font-semibold text-sm mono-nums">{w.currency} {Number(w.gross_amount ?? w.amount ?? 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-500 mono-nums">{w.currency} {Number(w.total_fee ?? w.fee_amount ?? w.fee ?? 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm mono-nums">{w.currency} {Number(w.net_amount ?? 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-700">{w.bank_name || w.mobile_money_number}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <StatusBadge status={w.status} />
                          <SettlementBadge withdrawal={w} />
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{new Date(w.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="text-xs" onClick={() => setDetailWithdrawal(w)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detailWithdrawal} onOpenChange={v => { if (!v) setDetailWithdrawal(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Withdrawal Details</DialogTitle>
          </DialogHeader>
          {detailWithdrawal && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ['Amount', formatMoney(detailWithdrawal.currency || 'UGX', detailWithdrawal.gross_amount ?? detailWithdrawal.amount ?? 0)],
                  ['Fee', formatMoney(detailWithdrawal.currency || 'UGX', detailWithdrawal.total_fee ?? detailWithdrawal.fee_amount ?? detailWithdrawal.fee ?? 0)],
                  ['Net', formatMoney(detailWithdrawal.currency || 'UGX', detailWithdrawal.net_amount ?? 0)],
                  ['Method', (detailWithdrawal.withdrawal_method || '').replace('_', ' ') || '-'],
                  ['Status', detailWithdrawal.status || '-'],
                  ['Payout Status', detailWithdrawal.payout_status || 'not_started'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] uppercase text-slate-500">{label}</p>
                    <p className="font-medium text-slate-900 mt-1 break-words">{value}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                <p className="text-xs uppercase text-blue-700 mb-2">Settlement Summary</p>
                <div className="flex flex-wrap items-center gap-2">
                  <SettlementBadge withdrawal={detailWithdrawal} />
                  <span className="text-sm text-blue-900">
                    {detailWithdrawal?.settled_directly_to_merchant || String(detailWithdrawal?.settlement_mode || '').toLowerCase() === 'flutterwave_split'
                      ? 'Money was settled directly to the merchant subaccount.'
                      : String(detailWithdrawal?.settlement_mode || '').toLowerCase() === 'jjuma_wallet'
                        ? 'Money was credited to the JJuma wallet balance.'
                        : 'The payout is still waiting on settlement processing.'}
                  </span>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase text-slate-500 mb-2">Account</p>
                {detailWithdrawal.withdrawal_method === 'bank_transfer' ? (
                  <div className="space-y-1 text-slate-700">
                    <p>Bank: {bankDetails?.bank_name || '-'}</p>
                    <p>Account Name: {bankDetails?.account_name || '-'}</p>
                    <p>Account Number: {bankDetails?.account_number || '-'}</p>
                  </div>
                ) : (
                  <p className="text-slate-700">Mobile Money: {bankDetails?.mobile_money_number || '-'}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
